import React from 'react';
import {
  collectorFacts,
  collectorRecognition,
  collectorTemplate,
  collectorPresentation,
} from '@bilt/domain/collectorTemplate';
import { publicCardFacts, passportKind, passportEventDate } from '@bilt/domain/passportRegistry';
import { makeCardRecord } from '@bilt/domain/cardRecord';
import type { PassportCard } from '../../services/api/passportCards';
export default function PassportTypeBack({
  card,
  demo,
  compact = false,
}: {
  card: PassportCard;
  demo: boolean;
  compact?: boolean;
}) {
  const record = makeCardRecord(card, demo),
    kind = card.subject_type,
    adapter = passportKind(kind);
  const facts = card.subject
    ? compact
      ? collectorFacts(kind, 'back', card.subject.facts)
      : publicCardFacts(kind, card.subject.facts)
    : [];
  const recognition = collectorRecognition(card.subject?.card_summary?.badges);
  const fields = (keys?: string[]) =>
    facts
      .filter((f) => !keys || keys.includes(f.key))
      .slice(0, compact ? 4 : undefined)
      .map((f) => (
        <div key={f.key}>
          <dt>{f.key.replaceAll('_', ' ')}</dt>
          <dd>
            {kind === 'event' && ['start_at', 'end_at'].includes(f.key)
              ? passportEventDate(f.value, String(card.subject?.facts?.timezone || 'UTC'), true)
              : f.value}
          </dd>
        </div>
      ));
  const receipt = (
    <section className="pe-receipt">
      <span className="pe-kicker">{demo ? 'Sample receipt' : 'Collection record'}</span>
      <p>{record.state}</p>
      {card.earned_at && !demo && <time>{card.earned_at.split(/[T ]/)[0]}</time>}
    </section>
  );
  if (!card.subject)
    return (
      <section className="pe-back pe-private">
        <h3>Collected memory</h3>
        <p>Subject details are no longer public.</p>
        {!compact && receipt}
      </section>
    );
  if (compact) {
    const presentation = collectorPresentation(
      kind,
      card.subject.facts,
      card.subject.card_summary?.badges,
      card.design.story
    );
    return (
      <section
        className={`pe-back pe-back-${kind} pe-back-compact`}
        data-record-kind={kind}
        data-density={presentation.density}
      >
        <header>
          <span className="pe-kicker">{presentation.heading}</span>
          <h3>{card.title}</h3>
        </header>
        {presentation.back.length > 0 && (
          <dl className={`ct-reverse-facts ct-reverse-${kind}`}>
            {presentation.back.map((f) => (
              <div key={f.key}>
                <dt>{f.label}</dt>
                <dd>
                  {kind === 'event' && f.key === 'end_at'
                    ? passportEventDate(
                        f.value,
                        String(card.subject?.facts?.timezone || 'UTC'),
                        true
                      )
                    : f.value}
                </dd>
              </div>
            ))}
          </dl>
        )}
        {presentation.reverseRecognition.length > 0 && (
          <section className="pe-recognition-record">
            <span className="pe-kicker">Recognition</span>
            <ul>
              {presentation.reverseRecognition.map((r) => (
                <li key={r.id}>
                  <strong>{r.title}</strong>
                  {r.issuer && <small>{r.issuer}</small>}
                </li>
              ))}
            </ul>
          </section>
        )}
        {presentation.reverseStory && (
          <section className="ct-reverse-story">
            <span className="pe-kicker">{kind === 'vehicle' ? 'Build story' : 'Source notes'}</span>
            <p>{presentation.reverseStory}</p>
          </section>
        )}
        {presentation.density === 'minimal' && (
          <div className="ct-watermark" aria-hidden="true">
            B:
          </div>
        )}
        {card.earned_at && !demo && (
          <p className="pe-earned-evidence">
            {record.state} · {card.earned_at.split(/[T ]/)[0]}
          </p>
        )}
        <p className="pe-record-issuer">{record.issuer}</p>
      </section>
    );
  }
  return (
    <section
      className={`pe-back pe-back-${kind} ${compact ? 'pe-back-compact' : ''}`}
      data-record-kind={kind}
    >
      <header>
        <span className="pe-kicker">
          {collectorTemplate(kind)?.heading || adapter?.back.replaceAll('_', ' ') || 'Provenance'}
        </span>
        <h3>{card.title}</h3>
        {!compact &&
          card.subject.subtitle &&
          card.subject.subtitle !== card.subject.facts?.story && <p>{card.subject.subtitle}</p>}
      </header>
      {kind === 'vehicle' ? (
        <>
          <dl className="pe-build-record">
            {fields(['engine', 'transmission', 'drivetrain', 'chassis'])}
          </dl>
        </>
      ) : kind === 'event' ? (
        <>
          <dl className="pe-timeline">
            {fields(
              compact
                ? ['end_at', 'timezone', 'host', 'category']
                : ['start_at', 'end_at', 'timezone', 'host', 'venue', 'city', 'category']
            )}
          </dl>
        </>
      ) : kind === 'club' ? (
        <>
          <div className="pe-community-heading">Community profile</div>
          <dl className="pe-community-facts">{fields()}</dl>
        </>
      ) : kind === 'shop' ? (
        <>
          <dl className="pe-service-record">{fields()}</dl>
        </>
      ) : kind === 'place' ? (
        <div className="pe-postcard-back">
          <dl>{fields(['category', 'offering', 'exhibition', 'access'])}</dl>
          <address>{card.subject.facts?.address || card.subject.facts?.city || '-'}</address>
          {card.subject.facts?.operator && <p>Operated by {card.subject.facts.operator}</p>}
        </div>
      ) : kind === 'booth' ? (
        <>
          <div className="pe-exhibitor-heading">Exhibitor profile</div>
          <dl className="pe-exhibitor-facts">{fields()}</dl>
        </>
      ) : kind === 'activation' ? (
        <>
          <div className="pe-experience-heading">Participation record</div>
          <dl className="pe-experience-facts">{fields()}</dl>
        </>
      ) : kind === 'destination' ? (
        <>
          <dl className="pe-route-record">{fields()}</dl>
        </>
      ) : kind === 'checkpoint' ? (
        <>
          <div className="pe-criterion">
            <span>Published criterion</span>
            <p>{card.subject.facts?.criterion || 'See program requirements'}</p>
          </div>
          <dl className="pe-checkpoint-facts">
            {fields(['position', 'campaign_title', 'optional'])}
          </dl>
        </>
      ) : kind === 'commemorative' ? (
        <>
          <div className="pe-colophon">
            <p>
              {card.subject.facts?.story || card.design.story || 'An authored Bilt collectible.'}
            </p>
            <dl>{fields(['occasion', 'issuer', 'credits'])}</dl>
          </div>
        </>
      ) : (
        <dl>{fields()}</dl>
      )}
      {recognition.length > 0 && (
        <section className="pe-recognition-record">
          <span className="pe-kicker">Recognition</span>
          <ul>
            {recognition.slice(0, compact ? 2 : undefined).map((r) => (
              <li key={r.id}>
                <strong>{r.title}</strong>
                {r.issuer && <small>{r.issuer}</small>}
              </li>
            ))}
          </ul>
        </section>
      )}
      {compact && <p className="pe-record-issuer">{record.issuer}</p>}
      {!compact && receipt}
    </section>
  );
}
